import React, { useState } from "react";
import { Mic2, CalendarClock, Camera, Send, Check } from "lucide-react";
import { ProviderSelect } from "./ProviderSelect";

const API = process.env.REACT_APP_BACKEND_URL;
const PURPOSES = ["recording session locations", "general inquiry", "photoshoot", "rollout", "signing", "collab / feature", "press / media", "other"];
const BACKDROPS = [
  { name: "red", hex: "#d32f2f" },
  { name: "orange", hex: "#f57c00" },
  { name: "light green", hex: "#9ccc65" },
  { name: "blue", hex: "#1e6ef5" },
  { name: "purple", hex: "#8e44ad" },
  { name: "black", hex: "#111111" },
];

const Field = ({ label, children }) => (
  <label className="req-field">
    <span className="req-label">{label}</span>
    {children}
  </label>
);

const useSubmit = (path) => {
  const [state, setState] = useState("idle");
  const submit = async (body) => {
    setState("busy");
    try {
      const res = await fetch(`${API}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setState(res.ok ? "ok" : "error");
      return res.ok;
    } catch {
      setState("error");
      return false;
    }
  };
  return [state, submit];
};

export const MusicRequests = () => {
  const [open, setOpen] = useState("");
  const [studio, setStudio] = useState({ name: "", email: "", location: "", date: "", time: "", hours: "", equipment: "", special_requests: "", notes: "", provider_id: "" });
  const [consult, setConsult] = useState({ name: "", email: "", purpose: "general inquiry", details: "", preferred_date: "", preferred_time: "", notes: "", provider_id: "" });
  const [shoot, setShoot] = useState({ name: "", email: "", phone: "", location: "", date: "", time: "", people: "", shoot_studio: false, shoot_outdoor: false, concept: "", backdrop: false, backdrop_color: "", notes: "" });
  const [sState, sendStudio] = useSubmit("/api/studio-requests");
  const [cState, sendConsult] = useSubmit("/api/consulting-requests");
  const [pState, sendShoot] = useSubmit("/api/photoshoot-requests");

  const inp = (obj, set, key, props = {}) => (
    <input className="req-input" value={obj[key]} onChange={(e) => set({ ...obj, [key]: e.target.value })}
      data-testid={`req-${key}`} {...props} />
  );

  return (
    <section className="music-requests" data-testid="music-requests">
      <div className="req-toggle-row">
        <button className="btn-ghost" onClick={() => setOpen(open === "studio" ? "" : "studio")} data-testid="studio-request-toggle">
          <Mic2 size={16} /> Request a studio session
        </button>
        <button className="btn-ghost" onClick={() => setOpen(open === "consult" ? "" : "consult")} data-testid="consulting-request-toggle">
          <CalendarClock size={16} /> Request a consulting meeting
        </button>
        <button className="btn-ghost" onClick={() => setOpen(open === "shoot" ? "" : "shoot")} data-testid="photoshoot-request-toggle">
          <Camera size={16} /> Photoshoot booking
        </button>
      </div>

      {open === "studio" && (
        <form className="req-form" data-testid="studio-request-form"
          onSubmit={async (e) => { e.preventDefault(); await sendStudio(studio); }}>
          <h3 className="req-title">Studio session request</h3>
          <div className="req-grid">
            <Field label="Your name*">{inp(studio, setStudio, "name", { required: true })}</Field>
            <Field label="Email*">{inp(studio, setStudio, "email", { type: "email", required: true })}</Field>
            <Field label="Location*">{inp(studio, setStudio, "location", { required: true, placeholder: "City / studio" })}</Field>
            <Field label="Date*">{inp(studio, setStudio, "date", { type: "date", required: true })}</Field>
            <Field label="Time*">{inp(studio, setStudio, "time", { type: "time", required: true })}</Field>
            <Field label="Hours needed*">{inp(studio, setStudio, "hours", { required: true, placeholder: "e.g. 4" })}</Field>
          </div>
          <Field label="Equipment requirements">
            <textarea className="req-input" rows={2} value={studio.equipment} data-testid="req-equipment"
              onChange={(e) => setStudio({ ...studio, equipment: e.target.value })} placeholder="Mics, interfaces, instruments…" />
          </Field>
          <Field label="Special requests">
            <textarea className="req-input" rows={2} value={studio.special_requests} data-testid="req-special"
              onChange={(e) => setStudio({ ...studio, special_requests: e.target.value })} />
          </Field>
          <Field label="Notes / anything important">
            <textarea className="req-input" rows={2} value={studio.notes} data-testid="req-notes"
              onChange={(e) => setStudio({ ...studio, notes: e.target.value })} />
          </Field>
          <ProviderSelect type="studio" value={studio.provider_id}
            onChange={(v) => setStudio({ ...studio, provider_id: v })} />
          {sState === "ok" ? (
            <div className="shirt-claim-ok" data-testid="studio-request-ok"><Check size={16} /> Request sent — we'll email you.</div>
          ) : (
            <button className="btn-solid" type="submit" disabled={sState === "busy"} data-testid="studio-request-submit">
              <Send size={15} /> {sState === "busy" ? "Sending…" : "Send studio request"}
            </button>
          )}
          {sState === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
        </form>
      )}

      {open === "consult" && (
        <form className="req-form" data-testid="consulting-request-form"
          onSubmit={async (e) => { e.preventDefault(); await sendConsult(consult); }}>
          <h3 className="req-title">Consulting meeting request</h3>
          <div className="req-grid">
            <Field label="Your name*">{inp(consult, setConsult, "name", { required: true })}</Field>
            <Field label="Email*">{inp(consult, setConsult, "email", { type: "email", required: true })}</Field>
            <Field label="Purpose*">
              <select className="req-input" value={consult.purpose} data-testid="req-purpose"
                onChange={(e) => setConsult({ ...consult, purpose: e.target.value })}>
                {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Preferred date">{inp(consult, setConsult, "preferred_date", { type: "date" })}</Field>
            <Field label="Preferred time">{inp(consult, setConsult, "preferred_time", { type: "time" })}</Field>
          </div>
          <Field label="Details*">
            <textarea className="req-input" rows={3} required value={consult.details} data-testid="req-details"
              onChange={(e) => setConsult({ ...consult, details: e.target.value })} placeholder="Tell us what you need…" />
          </Field>
          <Field label="Notes / anything important">
            <textarea className="req-input" rows={2} value={consult.notes} data-testid="req-consult-notes"
              onChange={(e) => setConsult({ ...consult, notes: e.target.value })} />
          </Field>
          <ProviderSelect type="consulting" value={consult.provider_id}
            onChange={(v) => setConsult({ ...consult, provider_id: v })} />
          {cState === "ok" ? (
            <div className="shirt-claim-ok" data-testid="consulting-request-ok"><Check size={16} /> Request sent — we'll email you.</div>
          ) : (
            <button className="btn-solid" type="submit" disabled={cState === "busy"} data-testid="consulting-request-submit">
              <Send size={15} /> {cState === "busy" ? "Sending…" : "Send consulting request"}
            </button>
          )}
          {cState === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
        </form>
      )}

      {open === "shoot" && (
        <form className="req-form" data-testid="photoshoot-request-form"
          onSubmit={async (e) => {
            e.preventDefault();
            if (shoot.backdrop && !shoot.backdrop_color) return;
            await sendShoot(shoot);
          }}>
          <h3 className="req-title">Photoshoot booking</h3>
          <div className="req-grid">
            <Field label="Your name*">{inp(shoot, setShoot, "name", { required: true })}</Field>
            <Field label="Email*">{inp(shoot, setShoot, "email", { type: "email", required: true })}</Field>
            <Field label="Phone*">{inp(shoot, setShoot, "phone", { type: "tel", required: true })}</Field>
            <Field label="Location*">{inp(shoot, setShoot, "location", { required: true, placeholder: "City / studio / on-site" })}</Field>
            <Field label="Date*">{inp(shoot, setShoot, "date", { type: "date", required: true })}</Field>
            <Field label="Time*">{inp(shoot, setShoot, "time", { type: "time", required: true })}</Field>
            <Field label="How many people*">{inp(shoot, setShoot, "people", { type: "number", min: 1, max: 99, required: true, placeholder: "e.g. 3" })}</Field>
          </div>
          <div className="req-field">
            <span className="req-label">Shoot type</span>
            <div className="shoot-type-row">
              <label className="req-backdrop-toggle" data-testid="shoot-type-studio">
                <input type="checkbox" checked={shoot.shoot_studio}
                  onChange={(e) => setShoot({ ...shoot, shoot_studio: e.target.checked })} />
                <span>Studio shoot</span>
              </label>
              <label className="req-backdrop-toggle" data-testid="shoot-type-outdoor">
                <input type="checkbox" checked={shoot.shoot_outdoor}
                  onChange={(e) => setShoot({ ...shoot, shoot_outdoor: e.target.checked })} />
                <span>Outdoor shoot</span>
              </label>
            </div>
          </div>
          <Field label="Concept / creative direction">
            <textarea className="req-input" rows={2} value={shoot.concept} data-testid="req-concept"
              onChange={(e) => setShoot({ ...shoot, concept: e.target.value })} placeholder="Mood, references, wardrobe…" />
          </Field>
          <div className="req-field">
            <label className="req-backdrop-toggle" data-testid="req-backdrop-toggle">
              <input type="checkbox" checked={shoot.backdrop}
                onChange={(e) => setShoot({ ...shoot, backdrop: e.target.checked, backdrop_color: e.target.checked ? shoot.backdrop_color : "" })} />
              <span>I'd like a backdrop</span>
            </label>
            {shoot.backdrop && (
              <div className="backdrop-swatches" data-testid="backdrop-swatches">
                {BACKDROPS.map((c) => (
                  <button key={c.name} type="button"
                    className={`backdrop-swatch${shoot.backdrop_color === c.name ? " selected" : ""}`}
                    style={{ background: c.hex }}
                    onClick={() => setShoot({ ...shoot, backdrop_color: c.name })}
                    title={c.name}
                    aria-label={`Backdrop ${c.name}`}
                    data-testid={`backdrop-${c.name.replace(" ", "-")}`}>
                    {shoot.backdrop_color === c.name && <Check size={14} />}
                  </button>
                ))}
                <span className="backdrop-picked">{shoot.backdrop_color || "pick a color"}</span>
              </div>
            )}
          </div>
          <Field label="Notes / anything important">
            <textarea className="req-input" rows={2} value={shoot.notes} data-testid="req-shoot-notes"
              onChange={(e) => setShoot({ ...shoot, notes: e.target.value })} />
          </Field>
          <ProviderSelect type="photoshoot" value={shoot.provider_id}
            onChange={(v) => setShoot({ ...shoot, provider_id: v })} />
          {pState === "ok" ? (
            <div className="shirt-claim-ok" data-testid="photoshoot-request-ok"><Check size={16} /> Booking request sent — we'll email you.</div>
          ) : (
            <button className="btn-solid" type="submit" disabled={pState === "busy" || (shoot.backdrop && !shoot.backdrop_color)} data-testid="photoshoot-request-submit">
              <Send size={15} /> {pState === "busy" ? "Sending…" : "Send booking request"}
            </button>
          )}
          {pState === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
        </form>
      )}
    </section>
  );
};
